import { Canvas, useFrame, useThree, useLoader } from '@react-three/fiber';
import {
  useVideoTexture,
  useGLTF,
  Environment,
  Text,
  PositionalAudio,
  useTexture,
} from '@react-three/drei';
import {
  useEffect,
  Suspense,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { SimplexNoise } from 'three/examples/jsm/math/SimplexNoise.js';
import * as THREE from 'three';
import { DoubleSide, Vector3, AudioLoader, AudioListener } from 'three';
// import { AudioLoader, AudioListener, PositionalAudio as PositionalAudioImpl } from 'three'
import { DFTPControls } from './Controls.js';
import {
  Bloom,
  EffectComposer,
  Outline,
  Selection,
  Select,
} from '@react-three/postprocessing';
import { useSpring, animated, config } from '@react-spring/three';

const useCheckMobileScreen = () => {
  const [width, setWidth] = useState(window.innerWidth);
  const handleWindowSizeChange = () => {
    setWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, []);

  return width <= 768;
};

const chatbotDict = {
  BOT_1: 1,
  BOT_2: 2,
  BOT_3: 3,
};

const botColors = [
  new THREE.Color('rgb(186,116,138)'),
  new THREE.Color('rgb(0,120,220)'),
  new THREE.Color('rgb(250,250,240)'),
  new THREE.Color('rgb(204,85,0)'),
];

// useGLTF.preload('/assets/binary-oracle/binary-oracle.glb');

const WaterBottle = ({ awaitingAnswer, waterOffering }) => {
  const { nodes, materials } = useGLTF(
    '/assets/binary-oracle/water-bottle-newlogo.glb'
  );
  const ref = useRef();
  const labelRef = useRef();

  const { scale, position } = useSpring({
    scale: awaitingAnswer ? 14 : 12,
    position: awaitingAnswer ? [0, -4, 0] : [0, 2, 0],
    config: {
      mass: 5,
      tension: 200,
      friction: 100,
    },
  });

  useFrame(() => {
    awaitingAnswer ? ref.current.rotateY(0.25) : ref.current.rotateY(0.025);
  }, []);
  return (
    <animated.group
      position={position}
      scale={scale}
      dispose={null}
      rotation={[0, 2.85, 0]}
      ref={ref}
    >
      <group position={[0, 0, 0.05]} rotation={[0.125, 0.0, 0.125]}>
        <mesh castShadow receiveShadow geometry={nodes.water001.geometry}>
          <meshStandardMaterial
            color={0xaaaaff}
            clipIntersection={true}
            clipShadows={true}
            side={DoubleSide}
            transparent
            opacity={0.5}
          >
            <plane
              attach="clippingPlanes-0"
              normal={[0, -1, 0]}
              constant={1.5 + waterOffering * 3.85}
            />
          </meshStandardMaterial>
        </mesh>
        <mesh
          ref={labelRef}
          castShadow
          receiveShadow
          geometry={nodes.label001.geometry}
          material={nodes.label001.material}
        ></mesh>
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.label002.geometry}
          material={nodes.label002.material}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.cap001.geometry}
          material={materials.CapMat}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.bottle001.geometry}
          material={materials.BottleMat}
          layers={[0, 10]}
        />
      </group>
    </animated.group>
  );
};

function splitTextIntoLines(text, maxLineLength) {
  const words = text.split(' ');
  let lines = [];
  let currentLine = '';

  words.forEach((word) => {
    if ((currentLine + word).length > maxLineLength) {
      lines.push(currentLine.trim());
      currentLine = '';
    }
    currentLine += word + ' ';
  });

  if (currentLine.length > 0) {
    lines.push(currentLine.trim());
  }

  return lines;
}

const OracleResponse = ({ text, persona, isLatest, index }) => {
  // Split the text into lines of at most 80 characters
  const lines = splitTextIntoLines(text, 80);
  const textToDisplayWithLineBreaks = lines.join('\n');

  const groupRef = useRef();

  const speed = isLatest ? -0.001 : Math.random() * 0.005;
  useFrame(() => {
    if (!groupRef.current) return;
    groupRef.current.rotateY(speed);
  });

  // const rotationOffset = useRef(Math.random() * -Math.PI);
  // const radiusOffset = useRef(Math.random() * -10 - 10);
  const rotationOffset = useRef(Math.random() * 6);
  const radiusOffset = useRef(-10 - Math.random() * 10);

  return (
    <>
      <group
        ref={groupRef}
        position={[0, isLatest ? 2 : 4, 0]}
        rotation={[0, isLatest ? -Math.PI : 0 + index * 2, 0]}
      >
        <Text
          position={[0, 0, isLatest ? 3 : radiusOffset.current]}
          color={botColors[Number(persona) - 1]}
          anchorX="center"
          anchorY="middle"
          fontSize={isLatest ? 0.125 : 0.25}
          curveRadius={isLatest ? -3 : -radiusOffset.current}
          outlineColor={0xdddddd}
          outlineWidth={0.005}
        >
          {textToDisplayWithLineBreaks}
        </Text>
      </group>
    </>
  );
};

const BinaryOracleInterface = ({
  setOracleResponses,
  setControlsEnabled,
  inDistance,
  awaitingAnswer,
  setAwaitingAnswer,
  offering,
  setOffering,
  totalWaterOffered,
  setTotalWaterOffered,
}) => {
  const [currentResponse, setCurrentResponse] = useState(null);
  const [question, setQuestion] = useState('');
  const [folded, setFolded] = useState(false);

  const inputRef = useRef();

  return (
    <div className={`offering-modal ${folded ? 'folded' : ''} `}>
      {folded && (
        <button
          className={`unstyled-button ${inDistance && folded ? 'pulsing' : ''}`}
          style={{
            left: '50%',
            transform: 'translate(-50%,0%)',
            position: 'relative',
          }}
          onClick={() => {
            setFolded(folded ? false : true);
          }}
        >
          ^
        </button>
      )}
      {!folded && (
        <button
          className={`unstyled-button`}
          style={{ width: '50px', position: 'absolute', right: '0px' }}
          onClick={() => {
            setFolded(true);
          }}
        >
          x
        </button>
      )}
      <div className="offering-modal-content">
        {awaitingAnswer && (
          <>
            <h3>The Binary Oracle will not be rushed...</h3>
          </>
        )}
        {currentResponse && (
          <>
            <div
              style={{ padding: '10px', maxHeight: '140px', overflow: 'auto' }}
            >
              <p>{currentResponse.answer.bot_answer}</p>
            </div>
            <button
              className="styled-button"
              onClick={() => {
                setCurrentResponse(null);
              }}
            >
              Ask again
            </button>
          </>
        )}
        {!question && !awaitingAnswer && !currentResponse && (
          <>
            <h3>
              The Binary Oracle awaits your question. <br />
              <em>Ask in any language.</em>
            </h3>

            <textarea
              ref={inputRef}
              name="Text1"
              cols="40"
              rows="4"
              placeholder="You ask.  The Oracle answers."
              onFocus={() => {
                setControlsEnabled(false);
              }}
              onBlur={() => {
                setControlsEnabled(true);
              }}
            ></textarea>

            <button
              className="styled-button"
              onClick={() => {
                setQuestion(inputRef.current.value);
              }}
            >
              I seek answers
            </button>
          </>
        )}
        {question && !awaitingAnswer && (
          <>
            <h3>
              How much water are you willing to give for the Oracle's insight?
            </h3>

            <input
              type="range"
              min="0"
              max="1"
              step={0.01}
              value={offering}
              onChange={(e) => setOffering(e.target.value)}
            />
            <button
              className="styled-button"
              onClick={async () => {
                setTotalWaterOffered(totalWaterOffered + Number(offering));

                setAwaitingAnswer(true);
                const response = await AskOracle({
                  offering: Number(offering),
                  question,
                  totalWaterOffered,
                });

                if (response !== null) {
                  setCurrentResponse(response);

                  setQuestion(null);
                  setOffering(0);
                  if (response == null) return; // TODO explain why null responses.
                  setOracleResponses((curr) => [...curr, response]);
                  setAwaitingAnswer(false);
                } else {
                  setCurrentResponse(null);
                  setQuestion(null);
                  setOffering(0);
                  setAwaitingAnswer(false);
                }
              }}
            >
              Make an offering
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export function BinaryOracleModel(props) {
  const { nodes } = useGLTF('/assets/binary-oracle/binary-oracle.glb');
  return (
    <group {...props} dispose={null}>
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.Pool.geometry}
        material={nodes.Pool.material}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.Water.geometry}
        material={nodes.Water.material}
        position={[0, -0.358, 0]}
        scale={0.952}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.Icosphere.geometry}
        material={nodes.Icosphere.material}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.Ground.geometry}
        material={nodes.Ground.material}
        scale={50}
      />
    </group>
  );
}

export const AskOracle = async ({ offering, question, totalWaterOffered }) => {
  // Define the URL of the endpoint
  const url = 'https://binaryoracle.dftp.live/ask_bot';

  // Define the parameters to send to the endpoint
  const query = {
    platform: 'OPENAI', // or OPENAI
    target_chatbot: 'BOT_1', // BOT_1 or BOT_2 or BOT_3
    user_question: question,
  };

  totalWaterOffered += offering;
  const REPEAT_VALUE = 6;

  if (totalWaterOffered % REPEAT_VALUE >= 5) {
    query['platform'] = 'RANDOM';
    query['target_chatbot'] = 'BOT_4';
  } else if (totalWaterOffered % REPEAT_VALUE >= 4) {
    query['platform'] = 'OPENAI';
    query['target_chatbot'] = 'BOT_5';
  } else if (totalWaterOffered % REPEAT_VALUE >= 3) {
    query['platform'] = 'GEMINI';
    query['target_chatbot'] = 'BOT_3';
  } else if (totalWaterOffered % REPEAT_VALUE >= 2) {
    query['platform'] = 'GEMINI';
    query['target_chatbot'] = 'BOT_2';
  } else if (totalWaterOffered % REPEAT_VALUE >= 1) {
    query['platform'] = 'OPENAI';
    query['target_chatbot'] = 'BOT_1';
  } else {
    query['platform'] = 'OPENAI';
    query['target_chatbot'] = 'BOT_5';
  }

  console.log(
    'Offering:',
    offering,
    '/ Total Water Offering:',
    totalWaterOffered
  );
  console.log('Querying persona ', query['target_chatbot']);

  // Send the POST request to the endpoint
  try {
    let response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(query),
    });
    let data = await response.json();
    return {
      persona:
        query['platform'] === 'RANDOM'
          ? 4
          : chatbotDict[query['target_chatbot']],
      answer: data,
    };
  } catch (error) {
    console.error('Error:', error);
    return null;
  }
};

export const GetLocalMediaPopup = ({
  localMediaStream,
  setLocalMediaStream,
}) => {
  const requestLocalMedia = useCallback(() => {
    navigator.mediaDevices
      .getUserMedia({ audio: false, video: true })
      .then((stream) => {
        if (stream) {
          console.log('Got local media stream!');
          setLocalMediaStream(stream);
        }
      })
      .catch((err) => {
        console.log('Could not getUserMedia:', err);
      });
  }, [setLocalMediaStream]);

  useEffect(() => {
    requestLocalMedia();
  }, []);

  return null;
};

const useMyVideoMaterial = ({ src }) => {
  const [videoElement] = useState(() => {
    console.log('creating video element');
    const video = document.createElement('video');
    video.muted = true;
    return video;
  });
  // const [videoTexture, setVideoTexture] = useState(null);
  const [videoMat, setVideoMat] = useState(
    new THREE.MeshBasicMaterial({ wireframe: true })
  );

  useEffect(() => {
    if (!videoElement || !src) return;
    console.log('adding src object to video element:', src);
    videoElement.srcObject = src;
    videoElement.onloadedmetadata = () => {
      videoElement.play();
    };
    const tex = new THREE.VideoTexture(videoElement);
    // setVideoTexture(tex);

    setVideoMat(new THREE.MeshBasicMaterial({ map: tex, side: DoubleSide }));
  }, [videoElement, src]);

  // useEffect(() => { console.log("got new src") }, [src]);

  // return meshBasicMaterial map={videoTexture} side={DoubleSide} />;
  return videoMat;
};

// Function to generate a random number between min and max
const randomInRange = (min, max) => {
  return Math.random() * (max - min) + min;
};

// Function to generate a random position within a sphere of radius 100
const randomPositionWithinSphere = () => {
  const radius = randomInRange(20, 50);
  const theta = Math.random() * Math.PI * 2;
  const phi = Math.acos(Math.random() * 2 - 1);
  const x = radius * Math.sin(phi) * Math.cos(theta);
  const y = radius * Math.sin(phi) * Math.sin(theta);
  const z = radius * Math.cos(phi);
  return new THREE.Vector3(x, y, z);
};

const RandomReflection = ({ videoMat, awaitingAnswer }) => {
  // useEffect(() => {
  //   console.log('random reflection got new localmediastream');
  // }, [localMediaStream])
  const [position] = useState(randomPositionWithinSphere());
  const ref = useRef();
  const speedRef = useRef(Math.random() / 100);
  const oldTarget = useRef(new Vector3());
  const oldTargetHasBeenSet = useRef(false);

  useFrame(({ camera }) => {
    if (awaitingAnswer) {
      ref.current.getWorldDirection(oldTarget.current);
      ref.current.lookAt(camera.position);
      oldTargetHasBeenSet.current = true;
    } else if (oldTargetHasBeenSet.current) {
      ref.current.lookAt(oldTarget.current);
      oldTargetHasBeenSet.current = false;
    } else {
      ref.current.rotateZ(speedRef.current);
      ref.current.rotateX(speedRef.current);
      ref.current.rotateY(speedRef.current);
    }
  });

  return (
    <mesh position={position} ref={ref} material={videoMat}>
      <planeGeometry />
    </mesh>
  );
};

const Reflections = ({ videoMat, awaitingAnswer }) => {
  const numTriangles = useMemo(() => Math.floor(randomInRange(505, 1000)), []);

  return (
    <>
      {Array.from({ length: numTriangles }).map((_, index) => {
        return (
          <RandomReflection
            key={index}
            videoMat={videoMat}
            awaitingAnswer={awaitingAnswer}
          />
        );
      })}
    </>
  );
};

export const ReflectingPool = ({ localMediaStream, props, awaitingAnswer }) => {
  const { nodes } = useGLTF('/assets/binary-oracle/binary-oracle.glb');

  const videoMat = useMyVideoMaterial({ src: localMediaStream });

  useEffect(() => {
    console.log('reflecting pool got new localmediastream');
  }, [localMediaStream]);

  const waterRef = useRef();
  const waterNoiseRef = useRef(new SimplexNoise());

  useFrame(({ clock }) => {
    if (!waterRef.current) return;

    const time = clock.getElapsedTime();
    const scale = 0.02;
    const waveScale = 2;
    const waveSpeed = 0.4;
    const offset = 0.5;
    const position = waterRef.current.geometry.attributes.position;

    for (let i = 0; i < position.count; i++) {
      const y = waterNoiseRef.current.noise(
        position.getX(i) * waveScale,
        time * waveSpeed,
        position.getZ(i) * waveScale
      );
      position.setY(i, offset + scale * y);
    }

    position.needsUpdate = true;
  });

  return (
    <group {...props} dispose={null}>
      <Reflections
        videoMat={videoMat}
        // localMediaStream={localMediaStream}
        awaitingAnswer={awaitingAnswer}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.Pool.geometry}
        material={nodes.Pool.material}
      ></mesh>
      <mesh
        ref={waterRef}
        castShadow
        receiveShadow
        geometry={nodes.Water.geometry}
        material={videoMat}
        position={[0, -0.358, 0]}
        scale={0.952}
      ></mesh>
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.Ground.geometry}
        material={nodes.Ground.material}
        scale={1}
      />
    </group>
  );
};

const ShowWhenWithinDistance = ({ setOracleInterfaceShown }) => {
  const origin = useMemo(() => new THREE.Vector3(0, 0, 0), []);
  useFrame(
    ({ camera }) => {
      if (camera.position.distanceTo(origin) < 15) {
        setOracleInterfaceShown(true);
      } else {
        setOracleInterfaceShown(false);
      }
    },
    [setOracleInterfaceShown]
  );

  return null;
};

const WaterBottleCounter = ({ totalWaterOffered, setShowResearch }) => {
  const { nodes, materials } = useGLTF(
    '/assets/binary-oracle/water-bottle-newlogo.glb'
  );
  const ref = useRef();
  const [bottlePositionsAndRotations, setBottlePositionsAndRotations] =
    useState([]);

  useEffect(() => {
    setBottlePositionsAndRotations((previousPositions) => {
      const newPositions = [...previousPositions];
      const total = Math.round(totalWaterOffered * 10);
      const diff = total - newPositions.length;
      for (let i = 0; i < diff; i++) {
        newPositions.push({
          position: [
            (Math.random() - 0.5) * 20,
            0.125,
            (Math.random() - 0.5) * 20,
          ],
          rotation: [Math.PI / 2, 0, Math.random() * 2 * Math.PI],
        });
      }
      return newPositions;
    });
  }, [totalWaterOffered, setBottlePositionsAndRotations]);

  return (
    <group ref={ref}>
      {bottlePositionsAndRotations.map((info, index) => {
        return (
          <group
            key={index}
            position={info.position}
            scale={[1, 1, 1]}
            rotation={info.rotation}
          >
            <mesh
              castShadow
              receiveShadow
              geometry={nodes.label001.geometry}
              material={materials.LabelMat}
            />
            <mesh
              castShadow
              receiveShadow
              geometry={nodes.label002.geometry}
              material={materials['LabelMat.001']}
            />
            <mesh
              castShadow
              receiveShadow
              geometry={nodes.cap001.geometry}
              material={materials.CapMat}
            />
            <mesh
              castShadow
              receiveShadow
              geometry={nodes.bottle001.geometry}
              material={materials.BottleMat}
              layers={[0, 10]}
              onClick={(e) => {
                setShowResearch(true);
              }}
              onPointerEnter={(e) => {
                document.getElementsByTagName('body')[0].style.cursor =
                  'pointer';
              }}
              onPointerLeave={(e) => {
                document.getElementsByTagName('body')[0].style.cursor = 'auto';
              }}
            />
          </group>
        );
      })}
    </group>
  );
};

export const MyPositionalAudio = ({
  url,
  distance = 1,
  loop = true,
  autoplay = true,
  shouldPlay = true,
  position = [0, 0, 0],
}) => {
  const sound = useRef();
  const listener = useThree(({ camera }) => {
    return camera.userData.listener;
  });
  const [soundLoaded, setSoundLoaded] = useState(false);

  useEffect(() => {
    console.log('mypositional audio setup:', url);

    sound.current.setRefDistance(distance);
    sound.current.setLoop(loop);

    const loader = new THREE.AudioLoader();

    // load a resource
    loader.load(
      // resource URL
      url,

      // onLoad callback
      function (audioBuffer) {
        // set the audio object buffer to the loaded object
        sound.current.setBuffer(audioBuffer);
        setSoundLoaded(true);
        console.log('sound loaded');
        if (autoplay && !sound.current.isPlaying) {
          sound.current.play();
          console.log('playing');
        }
      },

      // onProgress callback
      function (xhr) {
        // console.log(url,":",(xhr.loaded / xhr.total * 100) + '% loaded');
      },

      // onError callback
      function (err) {
        console.log('Error loading sound:', err);
      }
    );
  }, [url]);

  useEffect(() => {
    if (!sound.current || !soundLoaded) return;
    if (shouldPlay && !sound.current.isPlaying) {
      sound.current.play();
      console.log('starting to play sound!');
    } else if (!shouldPlay && sound.current.isPlaying) {
      sound.current.stop();
    }
  }, [shouldPlay, soundLoaded]);

  useEffect(() => {
    return () => {
      if (sound.current) {
        if (sound.current.isPlaying) sound.current.stop();
        if (sound.current.source && sound.current.source._connected)
          sound.current.disconnect();
      }
    };
  }, []);

  return <positionalAudio ref={sound} args={[listener]} position={position} />;
};

const MyAudio = ({ shouldPlaySound }) => {
  const { camera } = useThree();
  const [listener, setListener] = useState(null);

  useEffect(() => {
    const newListener = new THREE.AudioListener();
    camera.add(newListener);
    camera.userData.listener = newListener;
    setListener(newListener);

    return () => {
      camera.remove(newListener);
      camera.userData.listener = null;
    };
  }, [camera]);

  return (
    <>
      {listener && (
        <>
          <Suspense fallback={null}>
            <MyPositionalAudio
              url="/assets/binary-oracle/water.mp3"
              autoplay={false}
              shouldPlay={shouldPlaySound}
              position={[0, 0, 0]}
            />
          </Suspense>
          <Suspense fallback={null}>
            <MyPositionalAudio
              url="/assets/binary-oracle/ambient-audio-1.mp3"
              shouldPlay={true}
              position={[20, 0, 0]}
              distance={50}
            />
          </Suspense>
          <Suspense fallback={null}>
            <MyPositionalAudio
              url="/assets/binary-oracle/ambient-audio-2.mp3"
              shouldPlay={true}
              position={[0, 0, 10]}
              distance={50}
            />
          </Suspense>
          <Suspense fallback={null}>
            <MyPositionalAudio
              url="/assets/binary-oracle/ambient-audio-3.mp3"
              shouldPlay={true}
              position={[-20, 0, 0]}
              distance={50}
            />
          </Suspense>
        </>
      )}
    </>
  );
};

const Scene = ({
  initialized,
  localMediaStream,
  controlsEnabled,
  oracleResponses,
  setOracleInterfaceShown,
  awaitingAnswer,
  waterOffering,
  totalWaterOffered,
  setShowResearch,
}) => {
  const [shouldPlaySound, setShouldPlaySound] = useState(false);
  const timeoutRef = useRef(0);

  useEffect(() => {
    console.log('running should play loop');
    setShouldPlaySound(true);

    if (timeoutRef.current !== 0) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setShouldPlaySound(false);
    }, 500);
  }, [waterOffering]);
  return (
    <>
      <Canvas
        shadows={true}
        camera={{ position: [-2, 1.5, -12] }}
        gl={{ localClippingEnabled: true }}
      >
        {initialized && <MyAudio shouldPlaySound={shouldPlaySound} />}
        <fog attach="fog" args={['black', 10, 50]} />
        <color attach="background" args={['#000000']} />
        <ShowWhenWithinDistance
          setOracleInterfaceShown={setOracleInterfaceShown}
        />
        <WaterBottleCounter
          totalWaterOffered={totalWaterOffered}
          setShowResearch={setShowResearch}
        />

        <DFTPControls enabled={controlsEnabled} />

        <Selection>
          <EffectComposer autoClear={false}>
            <Bloom
              intensity={0.15} // The bloom intensity.
              luminanceThreshold={0.9} // luminance threshold. Raise this value to mask out darker elements in the scene.
              luminanceSmoothing={0.025} // smoothness of the luminance threshold. Range is [0, 1]
              mipmapBlur={false} // Enables or disables mipmap blur.
            />
            <Outline
              blur
              edgeStrength={2}
              bluriness={4}
              pulseSpeed={0.2} // a pulse speed. A value of zero disables the pulse effect
              visibleEdgeColor={0xffffff} // the color of visible edges
              hiddenEdgeColor={0x22090a} // the color of hidden edges
            />
          </EffectComposer>
          <Select enabled>
            <WaterBottle
              awaitingAnswer={awaitingAnswer}
              waterOffering={waterOffering}
            />
          </Select>
        </Selection>

        <Environment
          files="assets/binary-oracle/dikhololo_night_2k.exr"
          background={false}
        />
        <ambientLight intensity={0.5} />
        <spotLight
          color={'#ffffff'}
          intensity={2}
          position={[-20, 10, 20]}
          angle={0.25}
          penumbra={0.4}
          decay={0.3}
          castShadow
        />
        <directionalLight
          intensity={0.5}
          position={[20, 10, 20]}
          castShadow
          shadowBias={-0.005}
          shadow-camera-near={0.1}
          shadow-camera-far={100}
          shadow-camera-left={-30}
          shadow-camera-right={30}
          shadow-camera-top={30}
          shadow-camera-bottom={-30}
        />

        <ReflectingPool
          localMediaStream={localMediaStream}
          awaitingAnswer={awaitingAnswer}
        />

        {oracleResponses.map((resp, index) => {
          return (
            <OracleResponse
              text={resp.answer?.bot_answer}
              persona={resp.persona}
              isLatest={index === oracleResponses.length - 1}
              key={index}
              index={index}
            />
          );
        })}
      </Canvas>
    </>
  );
};

function getSavedOracleResponses() {
  let responses = [];
  let storedResponses = localStorage.getItem('oracleResponses');
  if (storedResponses) {
    responses = JSON.parse(storedResponses);
  }
  return responses;
}

export default function BinaryOracle({ initialized, setShowInfoPanel }) {
  const [localMediaStream, setLocalMediaStream] = useState(null);

  const usePreviousResponses = false;
  const [oracleResponses, setOracleResponses] = useState(
    usePreviousResponses ? getSavedOracleResponses() : []
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const liveMode = urlParams.has('liveMode');
    if (!liveMode) {
      // console.log('saving responses in regular mode');
      localStorage.setItem('oracleResponses', JSON.stringify(oracleResponses));
    } else {
      // console.log('not saving responses in live mode');
    }
  }, [oracleResponses]);

  const [hasShownResearch, setHasShownResearch] = useState(false);

  useEffect(() => {
    if (hasShownResearch || oracleResponses.length === 0) return; // we only do auto-popup once for research
    if (oracleResponses.length % 3 === 0) {
      let openTimeout = setTimeout(() => {
        setShowResearch(true);
      }, 5000);
      let closeTimeout = setTimeout(() => {
        setShowResearch(false);
      }, 30000);

      return () => {
        clearTimeout(openTimeout);
        clearTimeout(closeTimeout);
      };
    }
  }, [oracleResponses]);

  const [controlsEnabled, setControlsEnabled] = useState(true);

  const [inDistance, setInDistance] = useState(false);

  const [awaitingAnswer, setAwaitingAnswer] = useState(false);

  const [offering, setOffering] = useState(0);
  const [totalWaterOffered, setTotalWaterOffered] = useState(0);

  const [showResearch, setShowResearch] = useState(false);

  const isMobile = useCheckMobileScreen();

  return (
    <>
      <div id="canvas-container">
        <Scene
          initialized={initialized}
          localMediaStream={localMediaStream}
          oracleResponses={oracleResponses}
          controlsEnabled={controlsEnabled}
          setOracleInterfaceShown={setInDistance}
          awaitingAnswer={awaitingAnswer}
          waterOffering={offering}
          totalWaterOffered={totalWaterOffered}
          setShowResearch={setShowResearch}
        />
      </div>

      <div id="ui-container">
        {initialized && (
          <div
            style={{
              position: 'absolute',
              zIndex: '100',
              color: 'white',
              top: '20px',
              left: '20px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                color: 'white',
              }}
            >
              <button
                className={`unstyled-button`}
                style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  paddingBottom: '10px',
                }}
                onClick={() => setShowInfoPanel(true)}
              >
                ?
              </button>
              {!isMobile && (
                <>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <img
                      src="assets/icons/noun-keyboard-8537.svg"
                      width="60px"
                      height="60px"
                    />
                    <em>Move</em>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <img
                      src="assets/icons/noun-mouse-7106231.svg"
                      width="60px"
                      height="60px"
                    />
                    <em>Look</em>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {initialized && (
          <GetLocalMediaPopup
            localMediaStream={localMediaStream}
            setLocalMediaStream={setLocalMediaStream}
          />
        )}
        {initialized && (
          <BinaryOracleInterface
            setOracleResponses={setOracleResponses}
            setControlsEnabled={setControlsEnabled}
            inDistance={inDistance}
            awaitingAnswer={awaitingAnswer}
            setAwaitingAnswer={setAwaitingAnswer}
            offering={offering}
            setOffering={setOffering}
            totalWaterOffered={totalWaterOffered}
            setTotalWaterOffered={setTotalWaterOffered}
          />
        )}
        {showResearch && (
          <>
            <div className="research-modal">
              <div className="research-modal-content">
                <p>
                  <em>
                    "Training GPT-3 in Microsoft’s U.S. data centers can consume a total of 5.4 million liters of water ... Additionally, GPT-3 needs to “drink” (i.e., consume) a 500ml bottle of water for roughly 10 – 50 medium-length responses, depending on when and where it is deployed."
                  </em>
                  <br />

                  <br />
                  <p style={{ fontSize: '0.8em' }}>
                    <em>
                      (Li et al, "Making AI Less 'Thirsty'," arXiv, 2025)
                    </em>
                  </p>

                  <br />
                </p>
                <button
                  className="close-modal-button"
                  onClick={() => {
                    setShowResearch(false);
                  }}
                >
                  X
                </button>
              </div>
            </div>
          </>
        )}
        {initialized && (
          <>
            <div className="water-counter-hud">
              Total Water Used: {Math.round(totalWaterOffered * 10) / 10}{' '}
              Bottles
            </div>
          </>
        )}
      </div>
    </>
  );
}
